import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { styled, useTheme } from '@mui/material/styles';

import Dialog from 'components/dialog';
import Paper from 'components/paper';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import ListRow from 'components/list/list-row';
import ListCell from 'components/list/list-cell';
import List, { HeadCell } from 'components/list/list';

import Mui from 'materials';
import Highcharts from 'highcharts';
import titleCase from 'utils/title-case';
import _, { Dictionary } from 'underscore';
import HighchartsReact from 'highcharts-react-official';
import getLineGraphOptions from 'utils/get-line-graph-options';
import { FormattedDate, FormattedNumber, useIntl, FormattedMessage } from 'react-intl';

import { FilteredData, GraphData } from 'views/planning-prognosis/features/view-fish-group/types/default-fish-group-graph.type';
import dayjs from 'dayjs';
import { Box } from '@mui/material';

const ActionGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(3),
}));

type TableData = {
  name: string;
  date: string;
  value: number;
};

type Props = {
  graphs?: { name: string; data: GraphData[] }[];
  periodisation?: number;
  setPeriodisation?: (value: number) => void;
  snapshotData?: { name: string; data: GraphData[] }[];
};

const CalculatorGraph: React.FC<Props> = ({ graphs }) => {
  const [period, setPeriod] = useState<string>('daily');
  const [graphData, setGraphData] = useState<keyof GraphData>('feed');
  const [filteredData, setFilteredData] = useState<FilteredData>([]);

  const intl = useIntl();
  const theme = useTheme();

  const filterDataByPeriod = (period: string) => {
    let filterPeriodisation: FilteredData = graphs?.map((graph) => {
      return { ...graph, data: graph.data.filter((data) => data.periodType === period) };
    });

    setPeriod(period);
    setFilteredData(filterPeriodisation);
  };

  const keys = useMemo(() => {
    let graphKeys: string[] = [];

    if (!!filteredData?.length) {
      Object.entries(filteredData[0].data[0]).forEach(([k, v]) => {
        if (typeof v === 'number') graphKeys.push(k);
      });
    }

    return graphKeys;
  }, [filteredData]);

  const getTableData = useCallback((): Dictionary<TableData[]> => {
    const arr: TableData[] = [];

    if (!!filteredData?.length) {
      filteredData.forEach((g) => {
        let gArr: TableData[] = g.data.map((gData) => ({
          date: gData.date,
          value: gData[graphData] as number,
          name: g.name,
        }));

        arr.push(...gArr);
      });
    }

    return _.groupBy(arr, (d) => d.date);
  }, [filteredData, graphData]);

  const hasGraphData = !!filteredData?.length;

  const getLineStyles = (name: string) => {
    switch (name) {
      case 'Actual':
        return {
          dashStyle: 'Solid',
          color: '#017F62',
        };
      case 'Planned':
        return {
          dashStyle: 'ShortDot',
          color: theme.palette.bluViu.main,
        };
      case 'Prognosis':
        return {
          dashStyle: 'Solid',
          color: '#A63C25',
        };
      default:
        return {
          dashStyle: 'Solid',
          color: '#7D61B8',
        };
    }
  };

  useEffect(() => filterDataByPeriod(period), [period]);

  return (
    <Paper>
      {hasGraphData && (
        <ActionGroup>
          <Box minWidth={200} mr={3}>
            <Select
              hiddenLabel
              size="small"
              margin="none"
              label={'graphKeys'}
              fullWidth={false}
              value={graphData}
              style={{ minWidth: 150 }}
              onChange={(event) => setGraphData(event.target.value as keyof GraphData)}>
              {keys.map((key) => (
                <RadioSelectItem key={key} value={key} selected={key === graphData}>
                  {titleCase(key)}
                </RadioSelectItem>
              ))}
            </Select>
          </Box>
          <Box minWidth={100} mr={3}>
            <Select
              hiddenLabel
              size="small"
              value={period}
              margin="none"
              fullWidth={false}
              style={{ minWidth: 100 }}
              label={'filterDataByPeriod'}
              onChange={(event) => filterDataByPeriod(event.target.value as keyof GraphData)}>
              <Mui.MenuItem value={'daily'}>
                <FormattedMessage id="general.day" />
              </Mui.MenuItem>
              <Mui.MenuItem value={'weekly'}>
                <FormattedMessage id="general.week" />
              </Mui.MenuItem>
              <Mui.MenuItem value={'monthly'}>
                <FormattedMessage id="general.month" />
              </Mui.MenuItem>
            </Select>
          </Box>
          <Box>
            <Dialog maxWidth="sm" title="" button={<Mui.Button size="small">Show Table</Mui.Button>}>
              <List
                sx={{
                  '&. Table-tableHeadCell': {
                    color: theme.palette.darkBlue.main,
                  },
                }}
                options={[
                  { id: 'date', label: 'Date', align: 'left' },
                  ...filteredData
                    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                    .map((graph) => ({ id: graph.name.toLowerCase(), label: graph.name, align: 'right' } as HeadCell)),
                ]}>
                {Object.entries(getTableData()).map(([key, value]) => (
                  <ListRow key={key}>
                    <ListCell>
                      <FormattedDate value={key} day="2-digit" month="short" year="numeric" />
                    </ListCell>
                    <ListCell key={`${value[0].date}_${value[0].value}`} align="right">
                      <FormattedNumber value={value[0].value ?? 0} minimumFractionDigits={0} maximumFractionDigits={0} />
                    </ListCell>
                  </ListRow>
                ))}
              </List>
            </Dialog>
          </Box>
        </ActionGroup>
      )}
      {hasGraphData && (
        <HighchartsReact
          highcharts={Highcharts}
          options={
            {
              ...getLineGraphOptions({
                xAxisTitle: intl.formatMessage({ id: `general.date` }),
                yAxisTitle: intl.formatMessage({ id: `general.value` }),
                xAxisType: 'datetime',
                formatNumber: intl.formatNumber,
              }),
              series: filteredData?.map((graph) => ({
                ...getLineStyles(graph.name),
                name: graph.name,
                data: graph.data.map((point) => ({
                  x: dayjs(point.date).utc(true).valueOf(),
                  y: Number(Number(point[graphData]).toFixed(0)),
                })),
              })) as Highcharts.SeriesOptionsType[],
            } as Highcharts.Options
          }
        />
      )}
    </Paper>
  );
};

export default CalculatorGraph;
